import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { MediaPicker } from '@/components/admin/media-picker';
import { useAdminSession } from '@/lib/admin-session';
import { fetchAmbianceAssets, uploadAmbianceAsset } from '@/lib/api';
import { Button } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * L'image de fond du thème : le fichier de la médiathèque, choisi dans le
 * sélecteur partagé (refonte modulaire §9.4 ; maquette `BarreDeTheme.dc.html`).
 *
 * §9.4 gives the fond two forms, « une couleur ou une image de la médiathèque,
 * avec l'opacité d'un voile ». The colour is an `<input type="color">` in the
 * panel beside it; the image is THIS, and it is the existing `MediaPicker` and
 * not a select of filenames - « choisir un décor depuis `quai-nuit.webp` c'est
 * choisir une image sans la voir » is the whole reason that component exists.
 *
 * ## It holds its own query, and that is what makes the bar mountable anywhere
 *
 * The bar of §9.4 is one component at two levels (§9.1 for the experience, the
 * page editor of #58 for a page), and asking each host to fetch the media
 * library and pass it down would make the bar's props a list of things a host
 * has to remember. The library is a screen-wide read on a key the whole
 * administration can share, so the leaf that needs it reads it.
 *
 * ## Nothing is claimed about a file that is gone
 *
 * A file cited by a draft can have been deleted - §9.1 allows exactly that, and
 * makes « Enregistrer » refuse until it is replaced. So an id the library does
 * not answer for renders as « fichier supprimé, à remplacer » and not as a
 * blank: the refusal is already worded by the save box, and the bar's job is to
 * say which control to move.
 */

export interface ThemeBackgroundPickerProps {
  /** The file in force at this level, or null for « aucune image ». */
  readonly assetId: string | null;
  readonly readOnly: boolean;
  /** Null clears the binding - the picker's own « aucun fichier » (§9.4). */
  readonly onPick: (assetId: string | null) => void;
}

export function ThemeBackgroundPicker({ assetId, readOnly, onPick }: ThemeBackgroundPickerProps) {
  const { email } = useAdminSession();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);

  const assets = useQuery({
    queryKey: ['ambiance-assets', email],
    queryFn: fetchAmbianceAssets,
    retry: false,
  });

  const upload = useMutation({
    mutationFn: ({ file, license, tags }: { file: File; license: string; tags: string }) =>
      uploadAmbianceAsset(file, license, tags),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ambiance-assets', email] });
    },
  });

  const library = assets.data?.assets ?? [];
  const chosen = assetId === null ? null : (library.find((one) => one.id === assetId) ?? null);
  const missing = assetId !== null && chosen === null && assets.data !== undefined;

  return (
    <div data-testid={EXPERIENCE_IDS.themeBackground} className="flex flex-wrap items-center gap-2">
      <span className="min-w-0 break-all text-gray-800 text-small">
        {assetId === null ? (
          <span className="text-gray-600">aucune image</span>
        ) : missing ? (
          <span className="text-jaune-deep">fichier supprimé, à remplacer</span>
        ) : (
          (chosen?.filename ?? assetId)
        )}
      </span>

      {readOnly ? null : (
        <Button type="button" variant="accent" size="sm" onClick={() => setOpen(true)}>
          {assetId === null ? 'Choisir' : 'Remplacer'}
        </Button>
      )}

      {open ? (
        <MediaPicker
          title="Choisir l’image de fond de l’expérience"
          kind="image"
          assets={library}
          selectedId={assetId}
          emptyWording="aucune image"
          busy={upload.isPending}
          refusal={upload.isError ? 'Le fichier n’a pas pu être téléversé.' : null}
          onConfirm={(next) => {
            onPick(next);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
          onUpload={(file, license, tags) => upload.mutate({ file, license, tags })}
        />
      ) : null}
    </div>
  );
}
